import { PageHeader } from 'components/page-header';
import { useLocation } from 'react-router-dom';
import { EntityResponse, Notifications } from 'types/api-responses';
import { Helmet } from 'react-helmet-async';
import { pageTitle } from 'utils/page-title';
import {
  FlexGrid as Grid,
  Row,
  Column,
  Button,
  Tile,
  InlineLoading,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  ToastNotification,
} from '@carbon/react';
import style from './deduplicate.module.scss';
import { Add, SubtractAlt } from '@carbon/react/icons';
import { EntitySearchDialog } from 'pages/entities/entity-search-dialog';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'components/link';
import { useAtom } from 'jotai';
import { runMutationAtom } from './atoms';
import ErrorToast from 'components/error-toast';
import { subscribe } from 'lib/pusher';
import { EntityDetailsPopover } from 'components/entity-details-popover';
import { parseLocalDateTime } from 'utils/dates';
import type { EntityType } from 'lib/entities';
import type { EntitySearchResult } from 'pages/entities/list/types';
import {
  FlagOverrideModal,
  isFlagOverrideRequired,
  type FlaggedEntitySummary,
} from 'components/flag-override-modal';
import { EntityFlagTag } from 'components/entity-flag-tag';

type EntityResponseType = EntityResponse['type'];

const convertEntityType = (
  type: EntityResponseType | undefined
): EntityType | undefined => {
  if (!type) return undefined;
  const typeMap: Record<EntityResponseType, EntityType> = {
    IndividualEntity: 'Individual',
    OrganizationEntity: 'Organization',
    ImportedEntity: 'Imported',
  };
  return typeMap[type];
};

const EntityDeduplicationProgress = ({
  progress,
  onComplete,
}: {
  progress: { notifications: Notifications };
  onComplete: () => void;
}) => {
  const channel = progress.notifications.channel;
  const event = progress.notifications.events[0].name;
  const [pending, setPending] = useState(true);

  useEffect(() => {
    const subscription = subscribe(channel);
    subscription.bind(event, () => {
      onComplete();
      setPending(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [channel, event, onComplete]);

  return pending ? (
    <InlineLoading description="Running the deduplication..." />
  ) : null;
};

const SuccessToast = ({ onClose }: { onClose: () => boolean }) => {
  return (
    <ToastNotification
      className={style.toastNotification}
      title="Success deduplicating the entities"
      kind="success"
      lowContrast
      onClose={onClose}
      timeout={5000}
    />
  );
};

const PageContent = ({
  loading,
  entity,
}: {
  loading: boolean;
  entity: EntityResponse | undefined;
}) => {
  const [selectedEntities, setSelectedEntities] = useState<
    EntitySearchResult[]
  >([]);
  const { pathname } = useLocation();
  const [deduplicateEntities, runMutation] = useAtom(runMutationAtom);
  const [error, setError] = useState<string | undefined>(undefined);
  const [success, setSuccess] = useState(false);
  const [openSearch, setOpenSearch] = useState(false);
  const [notifications, setNotifications] = useState<
    { notifications: Notifications } | undefined
  >(undefined);

  const onDelete = useCallback(
    (index: number) => {
      setSelectedEntities((arr) => [
        ...arr.slice(0, index),
        ...arr.slice(index + 1),
      ]);
    },
    [setSelectedEntities]
  );

  const [flaggedForOverride, setFlaggedForOverride] = useState<
    FlaggedEntitySummary[] | null
  >(null);

  const onComplete = useCallback(() => {
    setSuccess(true);
    setSelectedEntities([]);
    setNotifications(undefined);
  }, []);

  const handleEntitySelect = useCallback((entities: EntitySearchResult[]) => {
    setSelectedEntities((current) => [...current, ...entities]);
  }, []);

  // A flagged entity in the selection returns a 422 asking for an override
  // rationale; passing one re-submits and proceeds (PROD/2907373590).
  const submitDeduplication = useCallback(
    (overrideRationale?: string, falsePositiveIds?: number[]) => {
      runMutation({
        url: pathname,
        type: 'POST',
        data: {
          duplicate_ids: selectedEntities.map((el) => el.id),
          ...(overrideRationale
            ? { override_rationale: overrideRationale }
            : {}),
          ...(falsePositiveIds?.length
            ? { false_positive_entity_ids: falsePositiveIds }
            : {}),
        },
        onSuccess: (response) => {
          setFlaggedForOverride(null);
          setNotifications(response);
        },
        onError: (data) => {
          if (isFlagOverrideRequired(data)) {
            setFlaggedForOverride(data.flaggedEntities);
          } else {
            setFlaggedForOverride(null);
            setError('Error trying to create the deduplication job');
          }
        },
      });
    },
    [runMutation, pathname, selectedEntities]
  );

  if (loading) return <InlineLoading />;
  if (!entity) return null;
  return (
    <Grid className="bx--no-gutter" fullWidth>
      {error && <ErrorToast key={error} message={error} />}
      {success && (
        <SuccessToast
          key={String(success)}
          onClose={() => {
            setSuccess(false);
            return true;
          }}
        />
      )}
      <FlagOverrideModal
        open={!!flaggedForOverride}
        flaggedEntities={flaggedForOverride ?? []}
        loading={deduplicateEntities.loading}
        onCancel={() => setFlaggedForOverride(null)}
        onConfirm={(rationale, falsePositiveIds) =>
          submitDeduplication(rationale, falsePositiveIds)
        }
      />
      <Tile>
        <Row>
          <Column lg={12}>
            <h2 className={style.subTitle}>Selected for deduplication</h2>

            <EntitySearchDialog
              isOpen={openSearch}
              onClose={() => setOpenSearch(false)}
              selectedEntityIds={selectedEntities.map((e) => e.id)}
              primaryEntityId={entity.id}
              primaryEntityType={convertEntityType(entity.type)}
              onSelect={handleEntitySelect}
            />
          </Column>
          <Column lg={4} className={style.actionsButtons}>
            <Button
              renderIcon={Add}
              kind="primary"
              size="md"
              onClick={() => setOpenSearch(true)}
            >
              Search Entities
            </Button>
            {notifications ? (
              <EntityDeduplicationProgress
                onComplete={onComplete}
                progress={notifications}
              />
            ) : (
              <Button
                kind="primary"
                size="md"
                disabled={
                  deduplicateEntities.loading || !selectedEntities.length
                }
                onClick={() => submitDeduplication()}
              >
                Submit
              </Button>
            )}
          </Column>
        </Row>
        <Row>
          <Column>
            <TableContainer className={style.dedupsTable}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader></TableHeader>
                    <TableHeader>Name</TableHeader>
                    <TableHeader>Id</TableHeader>
                    <TableHeader>Entity Type</TableHeader>
                    <TableHeader>Created Date</TableHeader>
                    <TableHeader>Updated Date</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedEntities.map((el, i) => {
                    return (
                      <TableRow key={el.id}>
                        <TableCell>
                          <div className={style.infoButtons}>
                            <Button
                              kind="ghost"
                              hasIconOnly
                              iconDescription="Remove Entity"
                              size="md"
                              renderIcon={(props) => (
                                <SubtractAlt size={20} {...props} />
                              )}
                              tooltipAlignment="start"
                              tooltipPosition="right"
                              onClick={() => onDelete(i)}
                            />
                            <EntityDetailsPopover href={`${el.href}/details`} />
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className={style.nameCell}>
                            <Link to={el.href}>{el.name}</Link>
                            <span className={style.nameChips}>
                              <EntityFlagTag
                                flagCategory={el.flagCategory}
                                flagRationale={el.flagRationale}
                                flagSeverity={el.flagSeverity}
                              />
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>{el.id}</TableCell>
                        <TableCell>{el.entityType}</TableCell>
                        <TableCell>
                          {el.createdAt
                            ? parseLocalDateTime(el.createdAt)
                            : '--'}
                        </TableCell>
                        <TableCell>
                          {el.updatedAt
                            ? parseLocalDateTime(el.updatedAt)
                            : '--'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Column>
        </Row>
      </Tile>
    </Grid>
  );
};

const Header = ({ entity }: { entity: EntityResponse | undefined }) => {
  const { pathname } = useLocation();
  if (!entity) return null;
  return (
    <PageHeader
      breadcrumbs={[
        ['/entities', 'Entities'],
        [entity.href, entity.name],
        [pathname, 'Deduplicate'],
      ]}
      title={entity.name || ''}
      actions={null}
    />
  );
};

const DeDuplicateEntityPage = ({
  entity,
  loading,
}: {
  entity: EntityResponse | undefined;
  loading: boolean;
}) => {
  return (
    <>
      <Header entity={entity} />
      {entity && (
        <Helmet>
          <title>{pageTitle(`${entity.name} | Deduplication`)}</title>
        </Helmet>
      )}
      <PageContent entity={entity} loading={loading} />
    </>
  );
};

export { DeDuplicateEntityPage };
